import axiosInstance from "../common/axiosInstance";
import { useQuery, UseQueryResult, useMutation, UseMutationResult } from "@tanstack/react-query";

export const PATHS = Object.freeze({
  GET_CALL_ANALYSIS: "/analytics/call-analysis",
  GET_CLIENT_ANALYSIS: "/analytics/client-relationship-analysis",
  GET_USER_ANALYTICS: "/analytics/user-analytics",
  GET_USER_ANALYTICS_NEW: "/analytics/user-analytics-new",
  GET_ANALYST_PROFILE: "/analytics/analyst-profile",
  GET_USER_STATS: "/analytics/get-user-stats",
  GET_INTERACTIONS_CALL_SCORE: "/analytics/call-scores",
  GET_INTERACTIONS_CALL_LIST: "/analytics/interaction-list",
  GET_INTERACTIONS_CALL_QUALITY_GRPH: "/analytics/call-quality-graph",
  GET_ACCOUNTS_FILTER_LIST: "/analytics/call-quality-graph-filter",
  GET_ANALYTICS_SALESFORCE_OPPORTUNITY_REPORT: "/analytics/salesforce-opportunity-report",
  UPDATE_ANALYTICS_SALESFORCE_OPPORTUNITY_REPORT: "/analytics/salesforce-opportunity-report/update",
  GET_SALESFORCE_OPPORTUNITY_REPORT_FILTERS:"/analytics/salesforce-mma-filter",
  GET_SALESFORCE_FILTER_PRESEARCH: "/analytics/salesforce-filter-presearch"
});

export interface GetCallAnalysisParams {
  employeePorId: string;
  isTeamAnalytics: boolean;
  startTime: string;
  endTime: string;
}

export interface GetUserAnalyticsParams {
  employeePorId: string;
  isTeamAnalytics: boolean;
  startTime: string;
  endTime: string;
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  interactionType?: string;
  isRated?: string;
  accountManagerView?: boolean;
}

export interface GetInteractionCallScoreParams {
  employeePorId: string;
  isForTeam: boolean;
  startTime: string;
  endTime: string;
  isForAccount?: boolean;
  interactionTypeFilter?: string;
}

export interface GetInteractionsCallListParams {
  employeePorId: string;
  isForTeam: boolean;
  startTime: string;
  endTime: string;
  score?: string;
  offset?: number;
  limit?: number;
  organizerPorId?: string;
  clientOrgId?: string;
  isForAccount?: boolean;
  arrCategory?: string;
  isOtherInteractionsFilter?: boolean;
  interactionTypeFilter?: string,
}

export interface GetCallQualityGraphParams {
  employeePorId: string;
  isForTeam: boolean;
  startTime: string;
  endTime: string;
  score?: string;
  callQualityGraphView?: string;
  isForAccount?: boolean;
  offset?: number;
  limit?: number;
  interactionTypeFilter?: string;
}

export interface GetAccountsFilterListParams {
  employeePorId: string;
  isForTeam: boolean;
  startTime: string;
  endTime: string;
  score?: string;
  callQualityGraphView?: string;
  isForAccount?: boolean;
  interactionTypeFilter?: string;
}

export interface GetAnalyticsSalesforceOpportunityReportParams {
  limit: number;
  offset: number;
  sortBy?: string;
  sortOrder?: string;
  stages?: string[];
  aeTeams?: string[];
  managerForecasts?: string[];
  recordTypes?: string[];
  dealTypes?: string[];
  closeDateStart?: string;
  closeDateEnd?: string;
  createdDateStart?: string;
  createdDateEnd?: string;
  arrAmount?: number;
  arrAmountOperator?: "gt" | "lt";
  opportunityName?: string | string[];
  buyerModes?: string[];
  l1Rygs?: string[];
  opportunitySources?: string[];
  marketPodLead?: string | string[];
  opportunityOwner?: string | string[];
  accountName?: string | string[];
  indiaSp?: string | string[];
  spPodLead?: string | string[];
}

export interface UpdateAnalyticsSalesforceOpportunityReportParams {
  opportunityId: string;
  whatIsOurBiggestHurdle?: string;
  managerForecastRationale?: string;
}

export interface GetSalesforceFilterPresearchParams {
  key: string;
  query?: string;
  ids?: string[];

  stages?: string[];
  aeTeams?: string[];
  managerForecasts?: string[];
  recordTypes?: string[];
  dealTypes?: string[];
  closeDateStart?: string;
  closeDateEnd?: string;
  createdDateStart?: string;
  createdDateEnd?: string;
  arrAmount?: number;
  arrAmountOperator?: "gt" | "lt";
  opportunityName?: string | string[];
  buyerModes?: string[];
  l1Rygs?: string[];
  opportunitySources?: string[];
  marketPodLead?: string | string[];
  opportunityOwner?: string | string[];
  accountName?: string | string[];
  indiaSp?: string | string[];
  spPodLead?: string | string[];
}

const getCallAnalysis = async (params: GetCallAnalysisParams): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_CALL_ANALYSIS,
    {
      employeePorId: params.employeePorId,
      isTeamAnalytics: params.isTeamAnalytics,
      startTime: params.startTime,
      endTime: params.endTime,
    },
  );
  return response;
};

const useGetCallAnalysis = (
  params: GetCallAnalysisParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_call_analysis", stringifiedParams],
    queryFn: () => getCallAnalysis(params),
    enabled,
    retry: 0,
  });
};

const getClientAnalysis = async (
  params: GetCallAnalysisParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_CLIENT_ANALYSIS,
    {
      employeePorId: params.employeePorId,
      isTeamAnalytics: params.isTeamAnalytics,
      startTime: params.startTime,
      endTime: params.endTime,
    },
  );
  return response;
};

const useGetClientAnalysis = (
  params: GetCallAnalysisParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_client_analysis", stringifiedParams],
    queryFn: () => getClientAnalysis(params),
    enabled,
    retry: 0,
  });
};

const getUserAnalytics = async (
  params: GetUserAnalyticsParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_USER_ANALYTICS,
    {
      employeePorId: params.employeePorId,
      isTeamAnalytics: params.isTeamAnalytics,
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.interactionType && { interactionType: params.interactionType }),
      ...(params.isRated && { isRated: params.isRated }),
    },
    {
      params: {
        ...(params.offset !== undefined && { offset: params.offset }),
        ...(params.limit !== undefined && { limit: params.limit }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortOrder && { sortOrder: params.sortOrder }),
      },
    }
  );
  return response;
};

const useGetUserAnalytics = (
  params: GetUserAnalyticsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_user_analytics", stringifiedParams],
    queryFn: () => getUserAnalytics(params),
    enabled,
    retry: 0,
  });
};

const getUserAnalyticsNew = async (params: GetUserAnalyticsParams): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_USER_ANALYTICS_NEW,
    {
      employeePorId: params.employeePorId,
      isTeamAnalytics: params.isTeamAnalytics,
      startTime: params.startTime,
      endTime: params.endTime,
      accountManagerView: params.accountManagerView,
    },
    {
      params: {
        ...(params.offset !== undefined && { offset: params.offset }),
        ...(params.limit !== undefined && { limit: params.limit }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortOrder && { sortOrder: params.sortOrder }),
      },
    }
  );
  return response;
};

const useGetUserAnalyticsNew = (
  params: GetUserAnalyticsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_user_analytics_new", stringifiedParams],
    queryFn: () => getUserAnalyticsNew(params),
    enabled,
    retry: 0,
  });
};

const getUserAnalystProfile = async(employeeEmail: string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ANALYST_PROFILE,{
    params: {employeeEmail}
  })
  return response.data;
}

const useGetUserAnalystProfile = (
  employeeEmail: string,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery<any>({
    queryKey: ["get_analyst_profile", employeeEmail],
    queryFn: () => getUserAnalystProfile(employeeEmail),
    enabled,
    retry: 0,
  });
};

const getUserStats = async(employeeEmail: string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_USER_STATS,{
    params: {employeeEmail}
  })
  return response.data;
}

const useGetUserStats = (
  employeeEmail: string,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery<any>({
    queryKey: ["get_user_stats", employeeEmail],
    queryFn: () => getUserStats(employeeEmail),
    enabled,
    retry: 0,
  });
};

const getInteractionsCallScore = async (
  params: GetInteractionCallScoreParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_INTERACTIONS_CALL_SCORE,
    {
      employeePorId: params.employeePorId,
      ...(params.isForTeam && { isForTeam: params.isForTeam }),
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.isForAccount && { isForAccount: params.isForAccount }),
      ...(params.interactionTypeFilter && { interactionTypeFilter: params.interactionTypeFilter }),
    },
  );

  return response;
};

const useGetInteractionsCallScore = (
  params: GetInteractionCallScoreParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_interactions_call_score", stringifiedParams],
    queryFn: () => getInteractionsCallScore(params),
    enabled,
    retry: 0,
  });
};

const getInteractionsCallList = async (
  params: GetInteractionsCallListParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_INTERACTIONS_CALL_LIST,
    {
      employeePorId: params.employeePorId,
      ...(params.isForTeam && { isForTeam: params.isForTeam }),
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.score && { score: params.score }),
      ...(params.organizerPorId && { organizerPorId: params.organizerPorId }),
      ...(params.clientOrgId && { clientOrgId: params.clientOrgId }),
      ...(params.isForAccount && { isForAccount: params.isForAccount }),
      ...(params.arrCategory && { arrCategory: params.arrCategory }),
      ...(params.isOtherInteractionsFilter && {
        isOtherInteractionsFilter: params.isOtherInteractionsFilter,
      }),
      ...(params.interactionTypeFilter && { interactionTypeFilter: params.interactionTypeFilter }),
    },
    {
      params: {
        ...(params.offset !== undefined && { offset: params.offset }),
        ...(params.limit !== undefined && { limit: params.limit }),
      },
    }
  );

  return response;
};
const useGetInteractionsCallList = (
  params: GetInteractionsCallListParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_interactions_call_list", stringifiedParams],
    queryFn: () => getInteractionsCallList(params),
    enabled,
    retry: 0,
  });
};

const getCallQualityGraph = async (
  params: GetCallQualityGraphParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_INTERACTIONS_CALL_QUALITY_GRPH,
    {
      employeePorId: params.employeePorId,
      ...(params.isForTeam && { isForTeam: params.isForTeam }),
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.score && { score: params.score }),
      ...(params.callQualityGraphView && {
        callQualityGraphView: params.callQualityGraphView,
      }),
      ...(params.isForAccount && { isForAccount: params.isForAccount }),
      ...(params.interactionTypeFilter && { interactionTypeFilter: params.interactionTypeFilter }),
    },
    {
      params: {
        ...(params.offset !== undefined && { offset: params.offset }),
        ...(params.limit !== undefined && { limit: params.limit }),
      },
    }
  );

  return response;
};

const useGetCallQualityGraph = (
  params: GetCallQualityGraphParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_call_quality_graph", stringifiedParams],
    queryFn: () => getCallQualityGraph(params),
    enabled,
    retry: 0,
  });
};

const getAccountsFilterList = async (
  params: GetAccountsFilterListParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_ACCOUNTS_FILTER_LIST,
    {
      employeePorId: params.employeePorId,
      ...(params.isForTeam && { isForTeam: params.isForTeam }),
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.score && { score: params.score }),
      ...(params.callQualityGraphView && {
        callQualityGraphView: params.callQualityGraphView,
      }),
      ...(params.isForAccount && {
        isForAccount: params.isForAccount,
      }),
      ...(params.interactionTypeFilter && {
        interactionTypeFilter: params.interactionTypeFilter,
      }),
    }
  );

  return response;
};

const useGetAccountsFilterList = (
  params: GetAccountsFilterListParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_accounts_filter_list", stringifiedParams],
    queryFn: () => getAccountsFilterList(params),
    enabled,
    retry: 0,
  });
};

const getAnalyticsSalesforceOpportunityReport = async (
  params: GetAnalyticsSalesforceOpportunityReportParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_ANALYTICS_SALESFORCE_OPPORTUNITY_REPORT,
    {
      ...(params.stages && params.stages.length > 0 && { stages: params.stages }),
      ...(params.aeTeams && params.aeTeams.length > 0 && { aeTeams: params.aeTeams }),
      ...(params.managerForecasts && params.managerForecasts.length > 0 && { managerForecasts: params.managerForecasts }),
      ...(params.recordTypes && params.recordTypes.length > 0 && { recordTypes: params.recordTypes }),
      ...(params.dealTypes && params.dealTypes.length > 0 && { dealTypes: params.dealTypes }),
      ...(params.closeDateStart && { closeDateStart: params.closeDateStart }),
      ...(params.closeDateEnd && { closeDateEnd: params.closeDateEnd }),
      ...(params.createdDateStart && { createdDateStart: params.createdDateStart }),
      ...(params.createdDateEnd && { createdDateEnd: params.createdDateEnd }),
      ...(params.arrAmount !== undefined && params.arrAmount !== null && { arrAmount: params.arrAmount }),
      ...(params.arrAmountOperator && { arrAmountOperator: params.arrAmountOperator }),
      ...(params.opportunityName && { opportunityIds: params.opportunityName }),
      ...(params.buyerModes && params.buyerModes.length > 0 && { buyerModes: params.buyerModes }),
      ...(params.l1Rygs && params.l1Rygs.length > 0 && { l1Rygs: params.l1Rygs }),
      ...(params.opportunitySources && params.opportunitySources.length > 0 && { opportunitySources: params.opportunitySources }),
      ...(params.marketPodLead && { marketPodLead: params.marketPodLead }),
      ...(params.opportunityOwner && { opportunityOwner: params.opportunityOwner }),
      ...(params.accountName && { accountIds: params.accountName }),
      ...(params.indiaSp && { indiaSp: params.indiaSp }),
      ...(params.spPodLead && { spPodLead: params.spPodLead }),
    },
    {
      params: {
        ...(params.offset !== undefined && { offset: params.offset }),
        ...(params.limit !== undefined && { limit: params.limit }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortOrder && { sortOrder: params.sortOrder }),
      },
    }
  );
  return response;
};

const useGetAnalyticsSalesforceOpportunityReport = (
  params: GetAnalyticsSalesforceOpportunityReportParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_analytics_salesforce_opportunity_report", stringifiedParams],
    queryFn: () => getAnalyticsSalesforceOpportunityReport(params),
    enabled,
    retry: 0,
  });
};

const updateAnalyticsSalesforceOpportunityReport = async (
  params: UpdateAnalyticsSalesforceOpportunityReportParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.UPDATE_ANALYTICS_SALESFORCE_OPPORTUNITY_REPORT,{
      opportunityId: params.opportunityId,
      ...(params.whatIsOurBiggestHurdle !== undefined && { whatIsOurBiggestHurdle: params.whatIsOurBiggestHurdle }),
      ...(params.managerForecastRationale !== undefined && { managerForecastRationale: params.managerForecastRationale }),
    }
  );
  return response;
};

const useUpdateAnalyticsSalesforceOpportunityReport = (
  handlerFunctions?: any
): UseMutationResult<any, any, UpdateAnalyticsSalesforceOpportunityReportParams> => {
  return useMutation({
    mutationKey: ["update_analytics_salesforce_opportunity_report"],
    mutationFn: updateAnalyticsSalesforceOpportunityReport,
    ...handlerFunctions,
  });
};

const getSalesforceOpportunityReportFilters = async (): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_SALESFORCE_OPPORTUNITY_REPORT_FILTERS);
  return response;
};

const useGetSalesforceOpportunityReportFilters = (
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery<any>({
    queryKey: ["get_salesforce_opportunity_report_filters"],
    queryFn: getSalesforceOpportunityReportFilters,
    enabled,
    retry: 0,
  });
};

const getSalesforceFiltersPresearch = async (params: GetSalesforceFilterPresearchParams): Promise<any> => {
  const response = await axiosInstance.post(PATHS.GET_SALESFORCE_FILTER_PRESEARCH,
    {
      ...(params.stages && params.stages.length > 0 && { stages: params.stages }),
      ...(params.aeTeams && params.aeTeams.length > 0 && { aeTeams: params.aeTeams }),
      ...(params.managerForecasts && params.managerForecasts.length > 0 && { managerForecasts: params.managerForecasts }),
      ...(params.recordTypes && params.recordTypes.length > 0 && { recordTypes: params.recordTypes }),
      ...(params.dealTypes && params.dealTypes.length > 0 && { dealTypes: params.dealTypes }),
      ...(params.closeDateStart && { closeDateStart: params.closeDateStart }),
      ...(params.closeDateEnd && { closeDateEnd: params.closeDateEnd }),
      ...(params.createdDateStart && { createdDateStart: params.createdDateStart }),
      ...(params.createdDateEnd && { createdDateEnd: params.createdDateEnd }),
      ...(params.arrAmount !== undefined && params.arrAmount !== null && { arrAmount: params.arrAmount }),
      ...(params.arrAmountOperator && { arrAmountOperator: params.arrAmountOperator }),
      ...(params.opportunityName && { opportunityIds: params.opportunityName }),
      ...(params.buyerModes && params.buyerModes.length > 0 && { buyerModes: params.buyerModes }),
      ...(params.l1Rygs && params.l1Rygs.length > 0 && { l1Rygs: params.l1Rygs }),
      ...(params.opportunitySources && params.opportunitySources.length > 0 && { opportunitySources: params.opportunitySources }),
      ...(params.marketPodLead && { marketPodLead: params.marketPodLead }),
      ...(params.opportunityOwner && { opportunityOwner: params.opportunityOwner }),
      ...(params.accountName && { accountIds: params.accountName }),
      ...(params.indiaSp && { indiaSp: params.indiaSp }),
      ...(params.spPodLead && { spPodLead: params.spPodLead }),
      ...(params.ids && { ids: params.ids }),
    },
    {
      params: {
        key: params.key,
        ...(params.query !== undefined && params.query !== null && { query: params.query }),
      }
    }
  );
  return response.data;
};

const useGetSalesforceFilterPresearch = (
  params: GetSalesforceFilterPresearchParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_salesforce_filter_presearch", stringifiedParams],
    queryFn: () => getSalesforceFiltersPresearch(params),
    enabled,
    retry: 0,
  });
};

export interface AnalyticalAnalysisService {
  readonly useGetCallAnalysis: (
    params: GetCallAnalysisParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetClientAnalysis: (
    params: GetCallAnalysisParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetUserAnalytics: (
    params: GetUserAnalyticsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetUserAnalyticsNew: (
    params: GetUserAnalyticsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  

  readonly useGetUserAnalystProfile: (
    employeeEmail: string,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetUserStats: (
    employeeEmail: string,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetInteractionsCallScore: (
    params: GetInteractionCallScoreParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetInteractionsCallList: (
    params: GetInteractionsCallListParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetCallQualityGraph: (
    params: GetCallQualityGraphParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetAccountsFilterList: (
    params: GetAccountsFilterListParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetAnalyticsSalesforceOpportunityReport: (
    params: GetAnalyticsSalesforceOpportunityReportParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpdateAnalyticsSalesforceOpportunityReport: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, UpdateAnalyticsSalesforceOpportunityReportParams>;

  readonly useGetSalesforceOpportunityReportFilters: (
    enabled: boolean
  ) => UseQueryResult<any, any>

  readonly useGetSalesforceFilterPresearch: (
    params: GetSalesforceFilterPresearchParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
} 

const AnalyticalAnalysisService: AnalyticalAnalysisService = Object.freeze({
  useGetCallAnalysis,
  useGetClientAnalysis,
  useGetUserAnalytics,
  useGetUserAnalyticsNew,
  useGetUserAnalystProfile,
  useGetUserStats,
  useGetInteractionsCallScore,
  useGetInteractionsCallList,
  useGetCallQualityGraph,
  useGetAccountsFilterList,
  useGetAnalyticsSalesforceOpportunityReport,
  useUpdateAnalyticsSalesforceOpportunityReport,
  useGetSalesforceOpportunityReportFilters,
  useGetSalesforceFilterPresearch,
});

export default AnalyticalAnalysisService;
